import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './Paint.module.css';
import { useWindowManager, WindowId } from '../Window/WindowManager';
import { useWindowResize } from '../Window/useWindowResize';
import { ResizeHandles } from '../Window/ResizeHandles';
import { WindowControls } from '../Window/WindowControls';
import { PaintbrushIcon } from '../icons';
import { useFileSystem, HOME_DIR } from '../Window/FileSystem';

const COLORS = [
  '#1A1A18', '#FFFFFF', '#DC3545', '#E8700A',
  '#FFC107', '#28A745', '#1A73E8', '#6F42C1',
  '#E83E8C', '#8B5E3C', '#8A8780', '#17A2B8',
];

const STROKE_SIZES = [
  { label: 'S', size: 2 },
  { label: 'M', size: 5 },
  { label: 'L', size: 10 },
  { label: 'XL', size: 20 },
];

interface PaintProps {
  initialPath?: string;
  initialContent?: string;
  openKey?: number;
}

export function Paint({ initialPath, initialContent, openKey }: PaintProps) {
  const {
    zIndex,
    focus,
    close,
    minimize,
    maximize,
    open,
    isMinimized,
    isClosed,
    isMaximized,
  } = useWindowManager(WindowId.Paint, 'Paint', <PaintbrushIcon />, 'closed');

  const { size, position, setPosition, isResizing, handleResizeStart } = useWindowResize(
    { width: 680, height: 420 },
    { x: 280, y: 80 },
    { minWidth: 340, minHeight: 280 }
  );

  // Dragging
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const paintRef = useRef<HTMLDivElement>(null);

  // Canvas
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);

  // Drawing state
  const [activeColor, setActiveColor] = useState(COLORS[6]);
  const [activeStroke, setActiveStroke] = useState(STROKE_SIZES[1].size);
  const [isErasing, setIsErasing] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [clipWarning, setClipWarning] = useState(false);

  // File state — tracks the path the user saved to, or the initial path from props.
  // When initialPath changes externally, we reset via key/remount isn't practical,
  // so we track changes with a callback from the auto-open effect.
  const [filePath, setFilePath] = useState<string | null>(initialPath || null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const fileNameInputRef = useRef<HTMLInputElement>(null);

  // Store drawing data for resize restoration
  // Store drawing as a data URL to preserve full resolution across resizes
  const imageDataRef = useRef<string | null>(null);

  // File system
  const { createFile, writeFile } = useFileSystem();

  // Track pending image to load once canvas is ready
  const pendingImageRef = useRef<string | null>(null);

  // Track whether an image is loading to prevent saving a blank canvas
  const isLoadingImageRef = useRef(false);

  // Helper to draw a data URL onto the canvas
  const drawDataUrl = useCallback((src: string) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    isLoadingImageRef.current = true;
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      imageDataRef.current = src;
      isLoadingImageRef.current = false;
    };
    img.src = src;
  }, []);

  // When a file is opened (openKey changes), load the image and open the window
  useEffect(() => {
    if (openKey === undefined || !initialPath || initialContent === undefined) return;
    imageDataRef.current = null;
    setFilePath(initialPath);
    setIsModified(false);
    open();

    // If canvas is already mounted, draw immediately; otherwise queue for resizeCanvas
    if (canvasRef.current) {
      drawDataUrl(initialContent);
    } else {
      pendingImageRef.current = initialContent;
    }
  }, [openKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus filename input when dialog opens
  useEffect(() => {
    if (showSaveDialog) {
      fileNameInputRef.current?.focus();
    }
  }, [showSaveDialog]);

  // Resize canvas to fill wrapper
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Save current drawing — skip if an image is mid-load (canvas would be blank)
    if (!isLoadingImageRef.current && canvas.width > 0 && canvas.height > 0) {
      imageDataRef.current = canvas.toDataURL('image/png');
    }

    const rect = wrapper.getBoundingClientRect();
    const newW = rect.width;
    const newH = rect.height;
    const oldW = canvas.width;
    const oldH = canvas.height;

    // Check if shrinking would clip drawn pixels
    if (!isLoadingImageRef.current && (newW < oldW || newH < oldH)) {
      const imgData = ctx.getImageData(0, 0, oldW, oldH).data;
      let hasClippedPixels = false;

      // Check right strip (pixels beyond newW)
      if (newW < oldW) {
        for (let y = 0; y < Math.min(oldH, newH) && !hasClippedPixels; y += 4) {
          for (let x = Math.floor(newW); x < oldW && !hasClippedPixels; x += 4) {
            const alpha = imgData[(y * oldW + x) * 4 + 3];
            if (alpha > 0) hasClippedPixels = true;
          }
        }
      }
      // Check bottom strip (pixels beyond newH)
      if (!hasClippedPixels && newH < oldH) {
        for (let y = Math.floor(newH); y < oldH && !hasClippedPixels; y += 4) {
          for (let x = 0; x < oldW && !hasClippedPixels; x += 4) {
            const alpha = imgData[(y * oldW + x) * 4 + 3];
            if (alpha > 0) hasClippedPixels = true;
          }
        }
      }

      if (hasClippedPixels) {
        setClipWarning(true);
      }
    }

    canvas.width = newW;
    canvas.height = newH;

    // Load pending image if one is queued (from opening a .png file)
    const pendingSrc = pendingImageRef.current;
    if (pendingSrc) {
      pendingImageRef.current = null;
      drawDataUrl(pendingSrc);
      return;
    }

    // Restore drawing
    if (imageDataRef.current) {
      drawDataUrl(imageDataRef.current);
    }
  }, [drawDataUrl]);

  // Observe wrapper for size changes (including initial mount)
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    resizeCanvas();

    const observer = new ResizeObserver(() => {
      resizeCanvas();
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [resizeCanvas, isClosed, isMinimized]);

  // Drawing handlers
  const getCanvasPoint = useCallback((e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const startDrawing = useCallback((e: React.PointerEvent) => {
    const point = getCanvasPoint(e);
    if (!point) return;
    isDrawing.current = true;
    lastPoint.current = point;

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.globalCompositeOperation = isErasing ? 'destination-out' : 'source-over';
    ctx.beginPath();
    ctx.arc(point.x, point.y, activeStroke / 2, 0, Math.PI * 2);
    ctx.fillStyle = isErasing ? 'rgba(0,0,0,1)' : activeColor;
    ctx.fill();
    setIsModified(true);
  }, [getCanvasPoint, activeColor, activeStroke, isErasing]);

  const draw = useCallback((e: React.PointerEvent) => {
    if (!isDrawing.current || !lastPoint.current) return;
    const point = getCanvasPoint(e);
    if (!point) return;

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    ctx.globalCompositeOperation = isErasing ? 'destination-out' : 'source-over';
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.strokeStyle = isErasing ? 'rgba(0,0,0,1)' : activeColor;
    ctx.lineWidth = activeStroke;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    lastPoint.current = point;
  }, [getCanvasPoint, activeColor, activeStroke, isErasing]);

  const stopDrawing = useCallback(() => {
    isDrawing.current = false;
    lastPoint.current = null;
  }, []);

  // Global pointerup to handle drawing stop outside canvas
  useEffect(() => {
    window.addEventListener('pointerup', stopDrawing);
    window.addEventListener('pointercancel', stopDrawing);
    return () => {
      window.removeEventListener('pointerup', stopDrawing);
      window.removeEventListener('pointercancel', stopDrawing);
    };
  }, [stopDrawing]);

  // Clear canvas
  const handleClear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    imageDataRef.current = null;
    setIsModified(true);
  }, []);

  // New blank canvas
  const handleNew = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    imageDataRef.current = null;
    setFilePath(null);
    setIsModified(false);
  }, []);

  // Save canvas to fake file system
  const handleSave = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');

    if (filePath) {
      // Overwrite existing file
      writeFile(filePath, dataUrl);
      setIsModified(false);
    } else {
      // Show save dialog for new file
      setShowSaveDialog(true);
      setSaveError(null);
      setNewFileName('');
    }
  }, [filePath, writeFile]);

  // Save new file from dialog
  const handleSaveNewFile = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let trimmedName = newFileName.trim();
    if (!trimmedName) {
      setSaveError('Please enter a file name');
      return;
    }

    if (/[/\\:*?"<>|]/.test(trimmedName)) {
      setSaveError('Invalid characters in file name');
      return;
    }

    // Ensure .png extension
    if (!trimmedName.endsWith('.png')) {
      // Strip any existing extension, then add .png
      const dotIndex = trimmedName.lastIndexOf('.');
      if (dotIndex > 0) {
        trimmedName = trimmedName.slice(0, dotIndex);
      }
      trimmedName += '.png';
    }

    const dataUrl = canvas.toDataURL('image/png');
    const success = createFile(HOME_DIR, trimmedName, dataUrl);

    if (success) {
      const newPath = `${HOME_DIR}/${trimmedName}`;
      setFilePath(newPath);
      setIsModified(false);
      setShowSaveDialog(false);
      setNewFileName('');
      setSaveError(null);
    } else {
      setSaveError('File already exists or cannot be created');
    }
  }, [newFileName, createFile]);

  // Handle save dialog key events
  const handleSaveDialogKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveNewFile();
    } else if (e.key === 'Escape') {
      setShowSaveDialog(false);
      setSaveError(null);
    }
  }, [handleSaveNewFile]);

  // Drag handling
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!paintRef.current) return;
    if (isMaximized) return;
    focus();
    const rect = paintRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);
  }, [focus, isMaximized]);

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const desktopEl = document.querySelector('[data-desktop]') as HTMLElement;
      if (!desktopEl) return;

      const desktopRect = desktopEl.getBoundingClientRect();
      const paintEl = paintRef.current;
      if (!paintEl) return;

      const paintWidth = paintEl.offsetWidth;
      const paintHeight = paintEl.offsetHeight;

      let newX = e.clientX - desktopRect.left - dragOffset.current.x;
      let newY = e.clientY - desktopRect.top - dragOffset.current.y;

      const dockHeight = 56;
      newX = Math.max(0, Math.min(newX, desktopRect.width - paintWidth));
      newY = Math.max(0, Math.min(newY, desktopRect.height - paintHeight - dockHeight));

      setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDragging, setPosition]);

  // Close animation
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClosing(true);
  };

  const handleAnimationEnd = useCallback((e: React.AnimationEvent) => {
    if (e.animationName === styles['windowClose']) {
      setIsClosing(false);
      setFilePath(null);
      setIsModified(false);
      close();
    }
    // Re-size canvas after open animation ends — during the animation,
    // scale(0.95) causes getBoundingClientRect() to return undersized values
    if (e.animationName === styles['windowOpen']) {
      resizeCanvas();
    }
  }, [close, resizeCanvas]);

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    minimize();
  };

  const handleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation();
    maximize();
  };

  // Display filename
  const displayName = filePath ? filePath.split('/').pop()! : 'Untitled';

  if (isClosed || isMinimized) {
    return null;
  }

  const windowStyle = isMaximized
    ? {
        left: 0,
        top: 0,
        width: '100%',
        height: 'calc(100% - 56px)',
        zIndex,
      }
    : {
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex,
      };

  return (
    <div
      ref={paintRef}
      className={`${styles.paint} ${isDragging || isResizing ? styles.dragging : ''} ${isMaximized ? styles.maximized : ''} ${isClosing ? styles.closing : ''}`}
      style={windowStyle}
      onPointerDown={focus}
      onAnimationEnd={handleAnimationEnd}
    >
      {!isMaximized && <ResizeHandles onResizeStart={handleResizeStart} />}

      {/* Title bar */}
      <div className={styles.paintHeader} onPointerDown={handlePointerDown}>
        <WindowControls
          onClose={handleClose}
          onMinimize={handleMinimize}
          onMaximize={handleMaximize}
        />
        <span className={styles.paintTitle}>{displayName}{isModified ? ' - (Unsaved changes)' : ''}</span>
      </div>

      {/* Toolbar */}
      <div className={styles.paintToolbar}>
        <div className={styles.toolbarSection}>
          {COLORS.map((color) => (
            <button
              key={color}
              className={`${styles.colorSwatch} ${!isErasing && activeColor === color ? styles.active : ''}`}
              style={{ background: color }}
              onClick={() => { setActiveColor(color); setIsErasing(false); }}
              aria-label={`Color ${color}`}
            />
          ))}
          <button
            className={`${styles.colorSwatch} ${styles.eraserSwatch} ${isErasing ? styles.active : ''}`}
            onClick={() => setIsErasing(true)}
            aria-label="Eraser"
          >
            <svg width={10} height={10} viewBox="0 0 10 10" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
              <line x1={2} y1={2} x2={8} y2={8} />
              <line x1={8} y1={2} x2={2} y2={8} />
            </svg>
          </button>
        </div>

        <div className={styles.toolbarDivider} />

        <div className={styles.toolbarSection}>
          {STROKE_SIZES.map(({ label, size: strokeSize }) => (
            <button
              key={label}
              className={`${styles.strokeButton} ${activeStroke === strokeSize ? styles.active : ''}`}
              onClick={() => setActiveStroke(strokeSize)}
              aria-label={`Stroke size ${label}`}
            >
              <span
                className={styles.strokeDot}
                style={{ width: Math.min(strokeSize, 16), height: Math.min(strokeSize, 16) }}
              />
            </button>
          ))}
        </div>

        <div className={styles.toolbarActions}>
          <button className={styles.clearButton} onClick={handleNew}>
            New
          </button>
          <button className={styles.clearButton} onClick={handleSave}>
            {filePath ? 'Save' : 'Save As'}
          </button>
          <button className={styles.clearButton} onClick={handleClear}>
            Clear
          </button>
        </div>
      </div>

      {/* Save dialog overlay */}
      {showSaveDialog && (
        <div className={styles.saveDialog}>
          <div className={styles.saveDialogContent}>
            <div className={styles.saveDialogTitle}>Save Drawing</div>
            <div className={styles.saveDialogField}>
              <label className={styles.saveDialogLabel}>File name:</label>
              <input
                ref={fileNameInputRef}
                type="text"
                className={styles.saveDialogInput}
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                onKeyDown={handleSaveDialogKeyDown}
                placeholder="drawing.png"
                autoComplete="off"
              />
            </div>
            <div className={styles.saveDialogLocation}>
              Saving to: ~/
            </div>
            {saveError && (
              <div className={styles.saveDialogError}>{saveError}</div>
            )}
            <div className={styles.saveDialogButtons}>
              <button
                className={styles.saveDialogButton}
                onClick={() => {
                  setShowSaveDialog(false);
                  setSaveError(null);
                }}
              >
                Cancel
              </button>
              <button
                className={`${styles.saveDialogButton} ${styles.primary}`}
                onClick={handleSaveNewFile}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div ref={wrapperRef} className={styles.paintCanvasWrapper}>
        {clipWarning && (
          <div className={styles.clipWarning}>
            <span>Warning: Content beyond the canvas edge will be lost.</span>
            <button
              className={styles.clipWarningClose}
              onClick={() => setClipWarning(false)}
              aria-label="Dismiss warning"
            >
              <svg width={8} height={8} viewBox="0 0 8 8" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
                <line x1={1} y1={1} x2={7} y2={7} />
                <line x1={7} y1={1} x2={1} y2={7} />
              </svg>
            </button>
          </div>
        )}
        <canvas
          ref={canvasRef}
          className={styles.paintCanvas}
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={stopDrawing}
        />
      </div>
    </div>
  );
}
